import { useState, useEffect } from "react";
import { AnimatePresence } from "framer-motion";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PageTransition } from "@/components/PageTransition";
import { BottomNavigation } from "@/components/BottomNavigation";
import { MenuDrawer } from "@/components/MenuDrawer";
import { useMessaging } from "@/hooks/useMessaging";
import { ChatList } from "@/components/messaging/ChatList";
import { ChatView } from "@/components/messaging/ChatView";

const Messaggi = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  const [autoAnswer, setAutoAnswer] = useState<"audio" | "video" | null>(null);

  const {
    chats, contacts, currentUserId, loading,
    addContactByEmail, createChat, sendMessage, fetchMessages,
    markAsRead, uploadMedia, getChatDisplayName, getChatAvatar,
  } = useMessaging();

  const activeChat = chats.find(c => c.id === activeChatId);

  // Open chat from notification query param
  useEffect(() => {
    const chatId = searchParams.get("chat");
    const answer = searchParams.get("answer") as "audio" | "video" | null;
    if (chatId && chats.some(c => c.id === chatId)) {
      setActiveChatId(chatId);
      setAutoAnswer(answer || null);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, chats, setSearchParams]);

  if (!currentUserId) {
    return (
      <PageTransition>
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <div className="text-center space-y-3">
            <p className="text-muted-foreground">Accedi per usare la messaggistica</p>
            <button onClick={() => navigate("/auth")} className="text-primary font-medium hover:underline">
              Vai al Login
            </button>
          </div>
        </div>
      </PageTransition>
    );
  }

  return (
    <PageTransition>
      <div
        className="min-h-screen bg-background/60 backdrop-blur-xl flex flex-col"
        style={{ height: "100dvh" }}
        data-tecly-private-chat={activeChat && !activeChat.is_group ? "true" : undefined}
      >
        <AnimatePresence mode="wait">
          {activeChat ? (
            <ChatView
              key="chat-view"
              chat={activeChat}
              currentUserId={currentUserId}
              displayName={getChatDisplayName(activeChat)}
              avatarUrl={getChatAvatar(activeChat)}
              onBack={() => { setActiveChatId(null); setAutoAnswer(null); }}
              onSendMessage={sendMessage}
              onFetchMessages={fetchMessages}
              onMarkAsRead={markAsRead}
              onUploadMedia={uploadMedia}
              autoAnswer={autoAnswer}
            />
          ) : (
            <ChatList
              key="chat-list"
              chats={chats}
              currentUserId={currentUserId}
              loading={loading}
              onSelectChat={setActiveChatId}
              getChatDisplayName={getChatDisplayName}
              getChatAvatar={getChatAvatar}
              onAddContact={addContactByEmail}
              contacts={contacts}
              onCreateChat={createChat}
            />
          )}
        </AnimatePresence>

        {!activeChat && (
          <>
            <BottomNavigation onMenuClick={() => setMenuOpen(true)} />
            <MenuDrawer open={menuOpen} onOpenChange={setMenuOpen} />
          </>
        )}
      </div>
    </PageTransition>
  );
};

export default Messaggi;
